import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import TaskHierarchyTree from './components/TaskHierarchyTree';
import TimelineGanttChart from './components/TimelineGanttChart';
import TaskDetailsPanel from './components/TaskDetailsPanel';
import DependencyMapModal from './components/DependencyMapModal';
import CriticalPathAnalysis from './components/CriticalPathAnalysis';
import ResourceAllocationPanel from './components/ResourceAllocationPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TaskDependencyAndTimelineManagement = () => {
  const { projectId } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState([]);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [viewMode, setViewMode] = useState('timeline');
  const [tasks, setTasks] = useState([
    {
      id: 1,
      name: "Foundation Module Assembly",
      category: "Structural",
      status: "completed",
      priority: "critical",
      duration: 120,
      actualHours: 118,
      progress: 100,
      workers: 8,
      startDate: "2026-01-06",
      endDate: "2026-01-20",
      dependencies: [],
      isMilestone: true,
      slack: 0,
      assignedTo: [
        { name: "James Wilson", role: "Structural Engineer" },
        { name: "Maria Garcia", role: "Assembly Lead" }
      ],
      notes: "Foundation modules completed ahead of schedule with excellent quality metrics.",
      subTasks: [
        {
          id: 11,
          name: "Steel Frame Welding",
          status: "completed",
          duration: 48,
          progress: 100,
          workers: 4,
          startDate: "2026-01-06",
          endDate: "2026-01-12",
          dependencies: []
        },
        {
          id: 12,
          name: "Concrete Pouring",
          status: "completed",
          duration: 40,
          progress: 100,
          workers: 6,
          startDate: "2026-01-13",
          endDate: "2026-01-18",
          dependencies: [11]
        }
      ]
    },
    {
      id: 2,
      name: "Wall Panel Fabrication",
      category: "Structural",
      status: "in-progress",
      priority: "high",
      duration: 96,
      actualHours: 52,
      progress: 65,
      workers: 12,
      startDate: "2026-01-21",
      endDate: "2026-02-03",
      dependencies: [1],
      isMilestone: false,
      slack: 0,
      assignedTo: [
        { name: "Robert Chen", role: "Fabrication Manager" },
        { name: "Lisa Anderson", role: "Quality Inspector" }
      ],
      notes: "Wall panels progressing on schedule. Material delivery confirmed for next phase.",
      subTasks: [
        {
          id: 21,
          name: "Insulation Installation",
          status: "in-progress",
          duration: 32,
          progress: 80,
          workers: 4,
          startDate: "2026-01-21",
          endDate: "2026-01-27",
          dependencies: []
        },
        {
          id: 22,
          name: "Exterior Cladding",
          status: "pending",
          duration: 40,
          progress: 0,
          workers: 6,
          startDate: "2026-01-28",
          endDate: "2026-02-03",
          dependencies: [21]
        }
      ]
    },
    {
      id: 3,
      name: "Electrical Systems Integration",
      category: "MEP",
      status: "in-progress",
      priority: "high",
      duration: 80,
      actualHours: 28,
      progress: 45,
      workers: 6,
      startDate: "2026-01-27",
      endDate: "2026-02-08",
      dependencies: [2],
      isMilestone: false,
      slack: 8,
      assignedTo: [
        { name: "David Martinez", role: "Electrical Engineer" },
        { name: "Sarah Johnson", role: "Systems Technician" }
      ],
      notes: "Electrical rough-in proceeding. Awaiting final fixture specifications from client.",
      subTasks: []
    },
    {
      id: 4,
      name: "Plumbing Installation",
      category: "MEP",
      status: "pending",
      priority: "medium",
      duration: 64,
      actualHours: 0,
      progress: 0,
      workers: 5,
      startDate: "2026-02-04",
      endDate: "2026-02-14",
      dependencies: [2],
      isMilestone: false,
      slack: 16,
      assignedTo: [
        { name: "Michael Brown", role: "Plumbing Supervisor" }
      ],
      notes: "Plumbing materials ordered. Installation scheduled to begin after wall panel completion.",
      subTasks: []
    },
    {
      id: 5,
      name: "HVAC System Assembly",
      category: "MEP",
      status: "pending",
      priority: "medium",
      duration: 72,
      actualHours: 0,
      progress: 0,
      workers: 4,
      startDate: "2026-02-09",
      endDate: "2026-02-21",
      dependencies: [3],
      isMilestone: false,
      slack: 8,
      assignedTo: [
        { name: "Jennifer Lee", role: "HVAC Specialist" }
      ],
      notes: "HVAC units delivered. Awaiting electrical system completion for installation.",
      subTasks: []
    },
    {
      id: 6,
      name: "Interior Finishing",
      category: "Finishing",
      status: "pending",
      priority: "low",
      duration: 88,
      actualHours: 0,
      progress: 0,
      workers: 10,
      startDate: "2026-02-15",
      endDate: "2026-03-01",
      dependencies: [4, 5],
      isMilestone: false,
      slack: 0,
      assignedTo: [
        { name: "Amanda White", role: "Interior Designer" },
        { name: "Carlos Rodriguez", role: "Finishing Crew Lead" }
      ],
      notes: "Interior finishing materials selected. Awaiting MEP completion to begin work.",
      subTasks: [
        {
          id: 61,
          name: "Drywall Installation",
          status: "pending",
          duration: 32,
          progress: 0,
          workers: 6,
          startDate: "2026-02-15",
          endDate: "2026-02-21",
          dependencies: []
        },
        {
          id: 62,
          name: "Painting",
          status: "pending",
          duration: 24,
          progress: 0,
          workers: 4,
          startDate: "2026-02-22",
          endDate: "2026-02-27",
          dependencies: [61]
        }
      ]
    },
    {
      id: 7,
      name: "Final Quality Inspection",
      category: "Quality Control",
      status: "pending",
      priority: "critical",
      duration: 16,
      actualHours: 0,
      progress: 0,
      workers: 3,
      startDate: "2026-03-02",
      endDate: "2026-03-04",
      dependencies: [6],
      isMilestone: true,
      slack: 0,
      assignedTo: [
        { name: "Dr. Emily Thompson", role: "Quality Assurance Manager" }
      ],
      notes: "Final inspection checklist prepared. All documentation ready for review.",
      subTasks: []
    },
    {
      id: 8,
      name: "Roof Module Installation",
      category: "Structural",
      status: "delayed",
      priority: "critical",
      duration: 56,
      actualHours: 32,
      progress: 40,
      workers: 8,
      startDate: "2026-01-28",
      endDate: "2026-02-10",
      dependencies: [2],
      isMilestone: false,
      slack: 0,
      assignedTo: [
        { name: "Thomas Anderson", role: "Roofing Specialist" }
      ],
      notes: "Roof installation delayed due to weather conditions. Recovery plan in place.",
      subTasks: []
    }
  ]);

  const criticalPath = [1, 2, 8, 6, 7];

  const timelineRange = {
    start: "2026-01-06",
    end: "2026-03-15"
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.altKey) {
        switch (e?.key?.toLowerCase()) {
          case 'e':
            if (selectedTask) {
              setShowDetailsPanel(true);
            }
            break;
          case 'd':
            if (selectedTask) {
              setShowDependencyModal(true);
            }
            break;
          case 'j': navigateTask('down');
            break;
          case 'k': navigateTask('up');
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedTask, tasks]);

  const navigateTask = (direction) => {
    if (!selectedTask) {
      setSelectedTask(tasks?.[0]);
      return;
    }

    const currentIndex = tasks?.findIndex(t => t?.id === selectedTask?.id);
    if (direction === 'down' && currentIndex < tasks?.length - 1) {
      setSelectedTask(tasks?.[currentIndex + 1]);
    } else if (direction === 'up' && currentIndex > 0) {
      setSelectedTask(tasks?.[currentIndex - 1]);
    }
  };

  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setShowDetailsPanel(true);
  };

  const handleTaskExpand = (taskId) => {
    setExpandedTasks(prev =>
      prev?.includes(taskId)
        ? prev?.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleTaskUpdate = (updatedTask) => {
    setTasks(tasks?.map(t => (t?.id === updatedTask?.id ? updatedTask : t)));
    setSelectedTask(updatedTask);
  };

  const handleDependencySave = (updatedTask) => {
    handleTaskUpdate(updatedTask);
  };

  const handleExportCriticalPath = () => {
    console.log('Exporting critical path report...');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <div style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                Task Dependency & Timeline Management
              </h1>
              <p className="caption text-muted-foreground mt-1">
                Production workflow orchestration with critical path analysis
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <NotificationCenter />
              
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                <Icon name="Calendar" size={16} color="var(--color-primary)" />
                <span className="text-sm text-foreground">
                  {new Date()?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'}
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 px-4 md:px-6 pb-4 overflow-x-auto">
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'outline'}
              size="sm"
              iconName="Calendar"
              onClick={() => setViewMode('timeline')}
            >
              Timeline
            </Button>
            <Button
              variant={viewMode === 'analysis' ? 'default' : 'outline'}
              size="sm"
              iconName="TrendingUp"
              onClick={() => setViewMode('analysis')}
            >
              Analysis
            </Button>
            <Button
              variant={viewMode === 'resources' ? 'default' : 'outline'}
              size="sm"
              iconName="Users"
              onClick={() => setViewMode('resources')}
            >
              Resources
            </Button>

            <div className="flex-1" />

            <Button
              variant="outline"
              size="sm"
              iconName="GitBranch"
              onClick={() => selectedTask && setShowDependencyModal(true)}
              disabled={!selectedTask}
            >
              Dependencies
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
            >
              Add Task
            </Button>
          </div>
        </header>

        <main className="p-4 md:p-6">
          {viewMode === 'timeline' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
              <div className="lg:col-span-3 h-[calc(100vh-240px)]">
                <TaskHierarchyTree
                  tasks={tasks}
                  selectedTask={selectedTask}
                  onTaskSelect={handleTaskSelect}
                  onTaskExpand={handleTaskExpand}
                  expandedTasks={expandedTasks}
                />
              </div>

              <div className={`${showDetailsPanel ? 'lg:col-span-6' : 'lg:col-span-9'} h-[calc(100vh-240px)]`}>
                <TimelineGanttChart
                  tasks={tasks}
                  selectedTask={selectedTask}
                  onTaskSelect={handleTaskSelect}
                  timelineRange={timelineRange}
                  onDependencyCreate={() => setShowDependencyModal(true)}
                />
              </div>

              {showDetailsPanel && (
                <div className="lg:col-span-3 h-[calc(100vh-240px)]">
                  <TaskDetailsPanel
                    task={selectedTask}
                    onTaskUpdate={handleTaskUpdate}
                    onClose={() => setShowDetailsPanel(false)}
                  />
                </div>
              )}
            </div>
          )}

          {viewMode === 'analysis' && (
            <div className="space-y-6">
              <CriticalPathAnalysis
                tasks={tasks}
                criticalPath={criticalPath}
                onExport={handleExportCriticalPath}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Task Status Distribution</h3>
                  <div className="space-y-3">
                    {[
                      { status: 'completed', count: tasks?.filter(t => t?.status === 'completed')?.length, color: 'var(--color-success)' },
                      { status: 'in-progress', count: tasks?.filter(t => t?.status === 'in-progress')?.length, color: 'var(--color-primary)' },
                      { status: 'pending', count: tasks?.filter(t => t?.status === 'pending')?.length, color: 'var(--color-warning)' },
                      { status: 'delayed', count: tasks?.filter(t => t?.status === 'delayed')?.length, color: 'var(--color-error)' }
                    ]?.map(item => (
                      <div key={item?.status}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-foreground capitalize">{item?.status?.replace('-', ' ')}</span>
                          <span className="text-sm font-semibold text-foreground">{item?.count}</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-250"
                            style={{
                              width: `${(item?.count / tasks?.length) * 100}%`,
                              backgroundColor: item?.color
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Priority Distribution</h3>
                  <div className="space-y-3">
                    {[
                      { priority: 'critical', count: tasks?.filter(t => t?.priority === 'critical')?.length, color: 'var(--color-error)' },
                      { priority: 'high', count: tasks?.filter(t => t?.priority === 'high')?.length, color: 'var(--color-warning)' },
                      { priority: 'medium', count: tasks?.filter(t => t?.priority === 'medium')?.length, color: 'var(--color-primary)' },
                      { priority: 'low', count: tasks?.filter(t => t?.priority === 'low')?.length, color: 'var(--color-success)' }
                    ]?.map(item => (
                      <div key={item?.priority}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-foreground capitalize">{item?.priority}</span>
                          <span className="text-sm font-semibold text-foreground">{item?.count}</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-250"
                            style={{
                              width: `${(item?.count / tasks?.length) * 100}%`,
                              backgroundColor: item?.color
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'resources' && (
            <ResourceAllocationPanel tasks={tasks} />
          )}

          <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <Icon name="Keyboard" size={20} color="var(--color-primary)" />
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Keyboard Shortcuts</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 caption text-muted-foreground">
                  <div><kbd className="px-2 py-1 bg-background rounded">Alt+E</kbd> Edit task</div>
                  <div><kbd className="px-2 py-1 bg-background rounded">Alt+D</kbd> Dependencies</div>
                  <div><kbd className="px-2 py-1 bg-background rounded">Alt+J</kbd> Next task</div>
                  <div><kbd className="px-2 py-1 bg-background rounded">Alt+K</kbd> Previous task</div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <QuickActionsToolbar />
      <DependencyMapModal
        isOpen={showDependencyModal}
        onClose={() => setShowDependencyModal(false)}
        task={selectedTask}
        allTasks={tasks}
        onSave={handleDependencySave}
      />
    </div>
  );
};

export default TaskDependencyAndTimelineManagement;